import { useMemo, useState } from "react";
import { useParams, useHistory } from "react-router";
import { AxiosError } from "axios";

import { DataConnector } from "@/api/data-connectors";
import { Button } from "@/design-system/components/Button/Button";
import { Skeleton } from "@/design-system/components/Skeleton/Skeleton";
import { useSetBreadcrumbs } from "@/NavBar/NavigationLayoutContext";
import PageWithTitle from "@/PageWithTitle";

import { useDataConnectorDetails, useSetDataConnectorStatus } from "../api";
import { DataConnectorBuilder } from "../components/DataConnectorBuilder/DataConnectorBuilder";
import { DataConnectorDetailHeader } from "../components/DataConnectorDetails/DataConnectorDetailHeader";
import { DataConnectorHelperCard } from "../components/DataConnectorDetails/DataConnectorHelperCard";
import { DataConnectorDisableConfirmation } from "../components/DataConnectorDisableConfirmation";

export default function DataConnectorDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const {
    data: dataConnector,
    isLoading,
    isError,
    error,
  } = useDataConnectorDetails(id);

  const title = useMemo(() => {
    if (isError) return "Data connector error";
    return dataConnector?.name ?? "View data connector";
  }, [dataConnector, isError]);

  return (
    <PageWithTitle title={title}>
      {isError ? (
        <DataConnectorDetailsErrorView error={error} />
      ) : isLoading || !dataConnector ? (
        <DataConnectorDetailsSkeleton />
      ) : (
        <DataConnectorView dataConnector={dataConnector} />
      )}
    </PageWithTitle>
  );
}

function DataConnectorDetailsErrorView({
  error,
}: {
  error: AxiosError | null;
}) {
  const history = useHistory();
  return (
    <div className="mx-auto max-w-3xl py-12 text-center">
      <p className="text-textSubtle mb-4 text-sm">
        {error?.message || "Could not load this data connector."}
      </p>
      <Button
        variant="outline"
        onClick={() => history.push("/destinations/data-connectors")}
      >
        Back to data connectors
      </Button>
    </div>
  );
}

function DataConnectorDetailsSkeleton() {
  return (
    <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-6 px-8 py-6 pb-16">
      <div className="flex items-start gap-6">
        <Skeleton className="h-16 w-16 rounded-xl" />
        <div className="flex-1 space-y-3">
          <Skeleton className="h-7 w-1/3" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/4" />
        </div>
      </div>
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

function DataConnectorView({
  dataConnector,
}: {
  dataConnector: DataConnector;
}) {
  const [builderOpen, setBuilderOpen] = useState(false);
  const [confirmingDisable, setConfirmingDisable] = useState(false);
  const setStatusMutation = useSetDataConnectorStatus();

  useSetBreadcrumbs([
    { label: "Destinations" },
    { label: "Data Connectors", href: "/destinations/data-connectors" },
    { label: dataConnector.name || "Data Connector", disabled: true },
  ]);

  const handleToggleStatus = () => {
    if (dataConnector.status === "active") {
      setConfirmingDisable(true);
      return;
    }
    setStatusMutation.mutate({ id: dataConnector.id, status: "active" });
  };

  const confirmDisable = () => {
    setStatusMutation.mutate(
      { id: dataConnector.id, status: "disabled" },
      { onSuccess: () => setConfirmingDisable(false) },
    );
  };

  return (
    <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-6 px-8 py-6 pb-16">
      <DataConnectorDetailHeader
        dataConnector={dataConnector}
        onEdit={() => setBuilderOpen(true)}
        onToggleStatus={handleToggleStatus}
        statusIsLoading={setStatusMutation.isLoading}
      />

      <DataConnectorHelperCard dataConnector={dataConnector} />

      <DataConnectorBuilder
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        dataConnector={dataConnector}
      />
      <DataConnectorDisableConfirmation
        dataConnector={confirmingDisable ? dataConnector : undefined}
        onConfirm={confirmDisable}
        onCancel={() => setConfirmingDisable(false)}
        isPending={setStatusMutation.isLoading}
      />
    </div>
  );
}
